import DOMPurify from 'dompurify';

const TIME_OPTS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

const ON_WROTE_ATTR_REGEX = /^On\s+[\s\S]+?\bwrote:\s*$/i;

const parseDate = (value?: string): Date | string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date;
};

const isSameCalendarDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const formatMessageDate = (value?: string) => {
  const parsed = parseDate(value);
  if (typeof parsed === 'string') return parsed;
  const now = new Date();
  if (isSameCalendarDay(parsed, now)) {
    return parsed.toLocaleTimeString([], TIME_OPTS);
  }
  if (parsed.getFullYear() === now.getFullYear()) {
    return parsed.toLocaleString([], { month: 'short', day: 'numeric', ...TIME_OPTS });
  }
  return parsed.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...TIME_OPTS,
  });
};

/** Compact list date: time / Yesterday / short date (Gmail-style). */
export const formatListDate = (value?: string) => {
  const parsed = parseDate(value);
  if (typeof parsed === 'string') return parsed;
  const now = new Date();
  if (isSameCalendarDay(parsed, now)) {
    return parsed.toLocaleTimeString([], TIME_OPTS);
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(parsed, yesterday)) {
    return 'Yesterday';
  }
  if (parsed.getFullYear() === now.getFullYear()) {
    return parsed.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
  return parsed.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

export const initials = (value?: string | null) => {
  const text = (value || '?').trim();
  if (!text) return '?';
  const parts = text.split(/[\s,]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return (text[0] ?? '?').toUpperCase();
};

export const avatarColor = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 42% 42%)`;
};

export const snippetFromBody = (body?: string | null) => {
  if (!body) return '';
  const text = body
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
};

export const sanitizeEmailHtml = (html?: string | null) => {
  if (!html) return '';
  try {
    return DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: [
        'script',
        'iframe',
        'object',
        'embed',
        'form',
        'input',
        'button',
        'img',
        'picture',
        'source',
        'video',
        'audio',
      ],
      ADD_ATTR: ['class', 'dir'],
      ALLOW_DATA_ATTR: false,
    });
  } catch {
    return '';
  }
};

export type SplitEmailBodyQuotesResult = {
  mainHtml: string;
  quoteHtml: string | null;
};

const getClassName = (el: Element) =>
  typeof el.className === 'string' ? el.className : el.getAttribute('class') || '';

const isGmailQuoteElement = (el: Element) => {
  const className = getClassName(el);
  return /\bgmail_quote\b/i.test(className) || /\bgmail_quote_container\b/i.test(className);
};

const isOnWroteAttribution = (el: Element) => {
  if (!['P', 'DIV', 'SPAN'].includes(el.tagName)) {
    return false;
  }
  // Avoid matching a huge wrapper whose textContent includes a nested "wrote:".
  if (el.querySelector('.gmail_quote, .gmail_quote_container, blockquote')) {
    return false;
  }
  const text = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
  return text.length > 0 && text.length < 300 && ON_WROTE_ATTR_REGEX.test(text);
};

const findQuoteStartElement = (root: Element): Element | null => {
  const gmail = root.querySelector('.gmail_quote, .gmail_quote_container');
  if (gmail) {
    return gmail;
  }

  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode();
  while (node) {
    const el = node as Element;
    if (isGmailQuoteElement(el) || isOnWroteAttribution(el)) {
      return el;
    }
    if (el.tagName === 'BLOCKQUOTE' && (el.textContent || '').trim().length > 0) {
      return el;
    }
    node = walker.nextNode();
  }

  return null;
};

const serializeFragment = (doc: Document, fragment: DocumentFragment) => {
  const wrap = doc.createElement('div');
  wrap.appendChild(fragment);
  return wrap.innerHTML.trim();
};

/**
 * Splits sanitized email HTML into the new message and the trailing quoted
 * section (gmail_quote / blockquote / "On … wrote:"), for Gmail-style collapse.
 * Handles quotes nested inside wrapper divs (typical Gmail / client HTML).
 */
export const splitEmailBodyQuotes = (html: string): SplitEmailBodyQuotesResult => {
  const trimmed = html?.trim() ?? '';
  if (!trimmed || typeof DOMParser === 'undefined') {
    return { mainHtml: trimmed, quoteHtml: null };
  }

  const doc = new DOMParser().parseFromString(
    `<div id="__email_root">${trimmed}</div>`,
    'text/html'
  );
  const root = doc.getElementById('__email_root');
  if (!root) {
    return { mainHtml: trimmed, quoteHtml: null };
  }

  const quoteStart = findQuoteStartElement(root);
  if (!quoteStart || !root.contains(quoteStart)) {
    return { mainHtml: trimmed, quoteHtml: null };
  }

  // Entire body is the quote — still allow collapse (empty main + quote).
  if (
    quoteStart === root.firstElementChild &&
    root.childElementCount === 1 &&
    isGmailQuoteElement(quoteStart)
  ) {
    return { mainHtml: '', quoteHtml: trimmed };
  }

  try {
    const mainRange = doc.createRange();
    mainRange.selectNodeContents(root);
    mainRange.setEndBefore(quoteStart);

    const quoteRange = doc.createRange();
    quoteRange.selectNodeContents(root);
    quoteRange.setStartBefore(quoteStart);

    const mainHtml = serializeFragment(doc, mainRange.cloneContents());
    const quoteHtml = serializeFragment(doc, quoteRange.cloneContents());

    if (!quoteHtml) {
      return { mainHtml: trimmed, quoteHtml: null };
    }

    // If almost everything landed in quote and main is empty whitespace, keep button.
    return { mainHtml, quoteHtml };
  } catch {
    return { mainHtml: trimmed, quoteHtml: null };
  }
};

/** Postmark / CRM delivery statuses for outbound history. */
export type OutboundDeliveryStatusTone = 'default' | 'success' | 'warning' | 'error' | 'info';

const OUTBOUND_DELIVERY_STATUS: Record<
  string,
  { label: string; tone: OutboundDeliveryStatusTone }
> = {
  queued: { label: 'Queued', tone: 'default' },
  sent: { label: 'Sent', tone: 'default' },
  delivered: { label: 'Delivered', tone: 'success' },
  opened: { label: 'Opened', tone: 'success' },
  bounced: { label: 'Bounced', tone: 'error' },
  failed: { label: 'Failed', tone: 'error' },
  spamcomplained: { label: 'Spam', tone: 'error' },
};

const resolveOutboundDeliveryStatus = (status?: string | null) => {
  const key = (status || '').trim().toLowerCase();
  if (!key) {
    return OUTBOUND_DELIVERY_STATUS.sent;
  }
  return OUTBOUND_DELIVERY_STATUS[key] ?? { label: status!.trim(), tone: 'info' as const };
};

export const formatOutboundDeliveryStatusLabel = (status?: string | null) =>
  resolveOutboundDeliveryStatus(status).label;

export const getOutboundDeliveryStatusTone = (status?: string | null): OutboundDeliveryStatusTone =>
  resolveOutboundDeliveryStatus(status).tone;
